// src/components/stylist/ServiceEditorForm.tsx
import React, { useState, useEffect } from 'react';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { yupResolver } from '../../utils/mockYupResolver';
import * as yup from 'yup';
import {
  DEFAULT_SERVICE_CATEGORIES,
  DEFAULT_HAIR_VARIATIONS,
  ServiceFormData
} from '../../types/service';

/**
 * Extend ServiceFormData to include 'subcategory' (the backend expects it)
 */
export interface ExtendedServiceFormData extends ServiceFormData {
  subcategory: string;
}

/**
 * Validation schema for the service editor form.
 */
const serviceSchema = yup.object().shape({
  name: yup.string().required('Service name is required'),
  description: yup.object().shape({
    short: yup.string().required('Description is required').max(100, 'Maximum 100 characters'),
    detailed: yup.string().optional().default('')
  }),
  category: yup.string().optional().default(''),
  subcategory: yup.string().optional().default(''),
  basePrice: yup.object().shape({
    min: yup.number()
      .typeError('Minimum price must be a number')
      .required('Minimum price is required')
      .min(0, 'Price cannot be negative'),
    max: yup.number()
      .typeError('Maximum price must be a number')
      .required('Maximum price is required')
      .min(0, 'Price cannot be negative')
      .test(
        'max-greater-than-min',
        'Max price must be greater than or equal to min price',
        function (value) {
          const { min } = this.parent;
          return min === undefined || value >= min;
        }
      )
  }),
  baseDuration: yup.object().shape({
    min: yup.number()
      .typeError('Minimum duration must be a number')
      .required('Minimum duration is required')
      .min(5, 'Duration must be at least 5 minutes'),
    max: yup.number()
      .typeError('Maximum duration must be a number')
      .required('Maximum duration is required')
      .min(5, 'Duration must be at least 5 minutes')
      .test(
        'max-greater-than-min',
        'Max duration must be greater than or equal to min duration',
        function (value) {
          const { min } = this.parent;
          return min === undefined || value >= min;
        }
      )
  }),
  variations: yup.array().of(
    yup.object().shape({
      id: yup.string().required(),
      name: yup.string().required('Variation name is required'),
      description: yup.string(),
      options: yup.array().of(
        yup.object().shape({
          id: yup.string().required(),
          name: yup.string().required('Option name is required'),
          description: yup.string(),
          additionalPrice: yup.number()
            .typeError('Additional price must be a number')
            .required('Additional price is required')
            .min(0, 'Price cannot be negative'),
          additionalDuration: yup.number()
            .typeError('Additional duration must be a number')
            .required('Additional duration is required')
            .min(0, 'Duration cannot be negative')
        })
      ).min(1, 'At least one option is required')
    })
  ),
  isActive: yup.boolean()
});

/**
 * Default values for a new service.
 */
const defaultValues: ExtendedServiceFormData = {
  name: '',
  description: {
    short: '',
    detailed: ''
  },
  category: '',
  subcategory: '',
  basePrice: {
    min: 0,
    max: 0
  },
  baseDuration: {
    min: 30,
    max: 30
  },
  variations: [],
  isActive: true
};

interface ServiceEditorFormProps {
  initialData?: ExtendedServiceFormData;
  onSubmit: (data: ExtendedServiceFormData) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`service-tabpanel-${index}`}
      aria-labelledby={`service-tab-${index}`}
      className="tesla-tab-panel"
      {...other}
    >
      {value === index && <div className="tesla-tab-panel__content">{children}</div>}
    </div>
  );
}

const ServiceEditorForm: React.FC<ServiceEditorFormProps> = ({ initialData, onSubmit, onCancel, isLoading = false }) => {
  const [tabValue, setTabValue] = useState(0);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors }
  } = useForm<ExtendedServiceFormData>({
    resolver: yupResolver(serviceSchema) as any,
    defaultValues: initialData || defaultValues,
    mode: 'onChange'
  });

  const { fields: variationFields, append: appendVariation, remove: removeVariation, update: updateVariation } =
    useFieldArray({
      control,
      name: 'variations'
    });

  // Ensure that maximum values are at least equal to minimum values
  const minPrice = watch('basePrice.min');
  const maxPrice = watch('basePrice.max');
  useEffect(() => {
    if (minPrice > maxPrice) {
      setValue('basePrice.max', minPrice);
    }
  }, [minPrice, maxPrice, setValue]);

  const minDuration = watch('baseDuration.min');
  const maxDuration = watch('baseDuration.max');
  useEffect(() => {
    if (minDuration > maxDuration) {
      setValue('baseDuration.max', minDuration);
    }
  }, [minDuration, maxDuration, setValue]);

  const handleFormSubmit = (data: ExtendedServiceFormData) => {
    // Single description field: sync detailed to short so API/consumers get one description (aligned with mobile)
    // When editing, ensure category/subcategory are sent (form may not show them; match mobile which doesn't send them but backend keeps existing)
    const payload = {
      ...data,
      description: {
        short: data.description.short,
        detailed: data.description.detailed?.trim() || data.description.short
      },
      category: data.category?.trim() || initialData?.category || '',
      subcategory: data.subcategory?.trim() || initialData?.subcategory || data.category?.trim() || initialData?.category || ''
    };
    onSubmit(payload);
  };

  const handleAddVariation = () => {
    appendVariation({
      id: `variation-${Date.now()}`,
      name: '',
      description: '',
      options: [
        {
          id: `option-${Date.now()}`,
          name: 'Base',
          description: '',
          additionalPrice: 0,
          additionalDuration: 0
        }
      ]
    });
  };

  const handleAddOption = (variationIndex: number) => {
    const current = watch(`variations.${variationIndex}`);
    updateVariation(variationIndex, {
      ...current,
      options: [
        ...current.options,
        {
          id: `option-${Date.now()}`,
          name: '',
          description: '',
          additionalPrice: 0,
          additionalDuration: 0
        }
      ]
    });
  };

  const handleRemoveOption = (variationIndex: number, optionIndex: number) => {
    const current = watch(`variations.${variationIndex}`);
    if (current.options.length <= 1) return;
    updateVariation(variationIndex, {
      ...current,
      options: current.options.filter((_: any, i: number) => i !== optionIndex)
    });
  };

  const handleAddHairTemplate = () => {
    DEFAULT_HAIR_VARIATIONS.forEach((variation) => {
      appendVariation(variation);
    });
  };

  const handleTabChange = (newIndex: number) => {
    setTabValue(newIndex);
  };

  return (
    <form className="service-editor" onSubmit={handleSubmit(handleFormSubmit)}>
      <div className="tesla-tabs service-editor__tabs">
        <button
          type="button"
          className={`tesla-tab ${tabValue === 0 ? 'tesla-tab--active' : ''}`}
          onClick={() => handleTabChange(0)}
        >
          Basic Information
        </button>
        <button
          type="button"
          className={`tesla-tab ${tabValue === 1 ? 'tesla-tab--active' : ''}`}
          onClick={() => handleTabChange(1)}
        >
          Pricing & Duration
        </button>
        <button
          type="button"
          className={`tesla-tab ${tabValue === 2 ? 'tesla-tab--active' : ''}`}
          onClick={() => handleTabChange(2)}
        >
          Variations
        </button>
      </div>

      {/* Tab Panel for Basic Information */}
      <TabPanel value={tabValue} index={0}>
        <div className="service-editor__grid">
          <div className="service-editor__field">
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <div className="tesla-form-group">
                  <label className="tesla-form-group__label">Service Name *</label>
                  <input
                    {...field}
                    type="text"
                    className={`tesla-input ${errors.name ? 'tesla-input--error' : ''}`}
                    placeholder="e.g. Men's Haircut"
                    disabled={isLoading}
                  />
                  {errors.name && <span className="tesla-form-group__error">{errors.name.message}</span>}
                </div>
              )}
            />
          </div>

          {/* Category and Subcategory hidden on edit to match mobile (values kept from initialData for submit) */}
          {!initialData?.name && (
            <>
              <div className="service-editor__field">
                <Controller
                  name="category"
                  control={control}
                  render={({ field }) => (
                    <div className="tesla-form-group">
                      <label className="tesla-form-group__label">Category *</label>
                      <div className="tesla-select-wrapper">
                        <select
                          {...field}
                          className={`tesla-select ${errors.category ? 'tesla-select--error' : ''}`}
                          disabled={isLoading}
                        >
                          <option value="">Select a category</option>
                          {DEFAULT_SERVICE_CATEGORIES.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.name}
                            </option>
                          ))}
                        </select>
                        <div className="tesla-select__arrow"></div>
                      </div>
                      {errors.category && <span className="tesla-form-group__error">{errors.category.message}</span>}
                    </div>
                  )}
                />
              </div>

              <div className="service-editor__field">
                <Controller
                  name="subcategory"
                  control={control}
                  render={({ field }) => (
                    <div className="tesla-form-group">
                      <label className="tesla-form-group__label">Subcategory *</label>
                      <input
                        {...field}
                        type="text"
                        className={`tesla-input ${errors.subcategory ? 'tesla-input--error' : ''}`}
                        placeholder="e.g. knotless-braids, blowouts, etc."
                        disabled={isLoading}
                      />
                      {errors.subcategory && <span className="tesla-form-group__error">{errors.subcategory.message}</span>}
                    </div>
                  )}
                />
              </div>
            </>
          )}

          <div className="service-editor__field">
            <Controller
              name="description.short"
              control={control}
              render={({ field }) => (
                <div className="tesla-form-group">
                  <label className="tesla-form-group__label">Description *</label>
                  <input
                    {...field}
                    type="text"
                    className={`tesla-input ${errors.description?.short ? 'tesla-input--error' : ''}`}
                    placeholder="Summary of the service (displayed on lists)"
                    disabled={isLoading}
                    maxLength={100}
                  />
                  {errors.description?.short && (
                    <span className="tesla-form-group__error">{errors.description.short.message}</span>
                  )}
                </div>
              )}
            />
          </div>

          <div className="service-editor__field">
            <Controller
              name="isActive"
              control={control}
              render={({ field }) => (
                <label className="tesla-toggle">
                  <input
                    type="checkbox"
                    className="tesla-toggle__input"
                    checked={field.value}
                    onChange={field.onChange}
                    disabled={isLoading}
                  />
                  <span className="tesla-toggle__slider"></span>
                  <span className="tesla-toggle__label">
                    Service is active and available for booking
                  </span>
                </label>
              )}
            />
          </div>
        </div>
      </TabPanel>

      {/* Tab Panel for Pricing & Duration */}
      <TabPanel value={tabValue} index={1}>
        <div className="service-editor__grid">
          <div className="service-editor__field service-editor__field--full">
            <h3 className="service-editor__subtitle">Base Price Range</h3>
            <p className="service-editor__description">
              The default price range before applying any variations.
            </p>
          </div>

          <div className="service-editor__field service-editor__field--half">
            <Controller
              name="basePrice.min"
              control={control}
              render={({ field }) => (
                <div className="form-group">
                  <label className="form-group__label">Minimum Price ($) *</label>
                  <div className="form-group__input-wrapper">
                    <input
                      {...field}
                      type="number"
                      value={field.value !== undefined ? String(field.value) : ''}
                      className={`form-group__input ${errors.basePrice?.min ? 'form-group__input--error' : ''}`}
                      disabled={isLoading}
                    />
                  </div>
                  {errors.basePrice?.min && (
                    <span className="form-group__error">{errors.basePrice.min.message}</span>
                  )}
                </div>
              )}
            />
          </div>

          <div className="service-editor__field service-editor__field--half">
            <Controller
              name="basePrice.max"
              control={control}
              render={({ field }) => (
                <div className="form-group">
                  <label className="form-group__label">Maximum Price ($) *</label>
                  <div className="form-group__input-wrapper">
                    <input
                      {...field}
                      type="number"
                      value={field.value !== undefined ? String(field.value) : ''}
                      className={`form-group__input ${errors.basePrice?.max ? 'form-group__input--error' : ''}`}
                      disabled={isLoading}
                    />
                  </div>
                  {errors.basePrice?.max && (
                    <span className="form-group__error">{errors.basePrice.max.message}</span>
                  )}
                </div>
              )}
            />
          </div>

          <div className="service-editor__field service-editor__field--full">
            <h3 className="service-editor__subtitle">Base Duration Range (minutes)</h3>
            <p className="service-editor__description">
              The default duration range before applying any variations.
            </p>
          </div>

          <div className="service-editor__field service-editor__field--half">
            <Controller
              name="baseDuration.min"
              control={control}
              render={({ field }) => (
                <div className="form-group">
                  <label className="form-group__label">Minimum Duration *</label>
                  <div className="form-group__input-wrapper">
                    <input
                      {...field}
                      type="number"
                      value={field.value !== undefined ? String(field.value) : ''}
                      className={`form-group__input ${errors.baseDuration?.min ? 'form-group__input--error' : ''}`}
                      disabled={isLoading}
                    />
                    <span className="form-group__suffix">min</span>
                  </div>
                  {errors.baseDuration?.min && (
                    <span className="form-group__error">{errors.baseDuration.min.message}</span>
                  )}
                </div>
              )}
            />
          </div>

          <div className="service-editor__field service-editor__field--half">
            <Controller
              name="baseDuration.max"
              control={control}
              render={({ field }) => (
                <div className="form-group">
                  <label className="form-group__label">Maximum Duration *</label>
                  <div className="form-group__input-wrapper">
                    <input
                      {...field}
                      type="number"
                      value={field.value !== undefined ? String(field.value) : ''}
                      className={`form-group__input ${errors.baseDuration?.max ? 'form-group__input--error' : ''}`}
                      disabled={isLoading}
                    />
                    <span className="form-group__suffix">min</span>
                  </div>
                  {errors.baseDuration?.max && (
                    <span className="form-group__error">{errors.baseDuration.max.message}</span>
                  )}
                </div>
              )}
            />
          </div>
        </div>
      </TabPanel>

      {/* Tab Panel for Variations */}
      <TabPanel value={tabValue} index={2}>
        <div className="service-editor__variation-header">
          <div>
            <h3 className="service-editor__subtitle">Service Variations</h3>
            <p className="service-editor__description">
              Add variations such as hair length or complexity, with multiple options for each.
            </p>
          </div>
          <div className="service-editor__variation-actions">
            <button
              type="button"
              className="service-editor__action-button"
              onClick={handleAddVariation}
              disabled={isLoading}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              Add New Variation
            </button>
            {variationFields.length === 0 && (
              <button
                type="button"
                className="service-editor__action-button service-editor__action-button--secondary"
                onClick={handleAddHairTemplate}
                disabled={isLoading}
              >
                Use Hair Service Template
              </button>
            )}
          </div>
        </div>

        {variationFields.length === 0 ? (
          <div className="service-editor__empty-state">
            <p className="service-editor__empty-title">No variations added yet</p>
            <p className="service-editor__empty-description">
              Add variations or click the button to use a hair template.
            </p>
          </div>
        ) : (
          variationFields.map((variation, vIndex) => {
            const currentVariation = watch(`variations.${vIndex}`);
            return (
              <div key={variation.id} className="variation-card">
                <div className="variation-card__header">
                  <h3 className="variation-card__title">Variation {vIndex + 1}</h3>
                  <button
                    type="button"
                    className="variation-card__delete"
                    onClick={() => removeVariation(vIndex)}
                    disabled={isLoading}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="3 6 5 6 21 6"></polyline>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                      <line x1="10" y1="11" x2="10" y2="17"></line>
                      <line x1="14" y1="11" x2="14" y2="17"></line>
                    </svg>
                  </button>
                </div>
                <div className="service-editor__grid service-editor__grid--compact">
                  <div className="service-editor__field service-editor__field--half">
                    <Controller
                      name={`variations.${vIndex}.name`}
                      control={control}
                      render={({ field }) => (
                        <div className="form-group">
                          <label className="form-group__label">Variation Name *</label>
                          <input
                            {...field}
                            type="text"
                            className={`form-group__input ${errors.variations?.[vIndex]?.name ? 'form-group__input--error' : ''}`}
                            placeholder="e.g. Hair Length"
                            disabled={isLoading}
                          />
                          {errors.variations?.[vIndex]?.name && (
                            <span className="form-group__error">
                              {(errors.variations[vIndex] as any).name?.message}
                            </span>
                          )}
                        </div>
                      )}
                    />
                  </div>
                  <div className="service-editor__field service-editor__field--half">
                    <Controller
                      name={`variations.${vIndex}.description`}
                      control={control}
                      render={({ field }) => (
                        <div className="form-group">
                          <label className="form-group__label">Variation Description</label>
                          <input
                            {...field}
                            type="text"
                            className={`form-group__input ${errors.variations?.[vIndex]?.description ? 'form-group__input--error' : ''}`}
                            placeholder="e.g. length-based variations"
                            disabled={isLoading}
                          />
                          {errors.variations?.[vIndex]?.description && (
                            <span className="form-group__error">
                              {(errors.variations[vIndex] as any).description?.message}
                            </span>
                          )}
                        </div>
                      )}
                    />
                  </div>
                </div>
                <div className="variation-card__divider"></div>
                <h4 className="variation-card__subtitle">Options</h4>
                {(currentVariation.options as any[]).map((option, oIndex) => (
                  <div key={option.id} className="option-card">
                    <div className="option-card__header">
                      <h5 className="option-card__title">Option {oIndex + 1}</h5>
                      <button
                        type="button"
                        className="option-card__delete"
                        onClick={() => handleRemoveOption(vIndex, oIndex)}
                        disabled={isLoading || (currentVariation.options as any[]).length <= 1}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="3 6 5 6 21 6"></polyline>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                          <line x1="10" y1="11" x2="10" y2="17"></line>
                          <line x1="14" y1="11" x2="14" y2="17"></line>
                        </svg>
                      </button>
                    </div>
                    <div className="service-editor__grid service-editor__grid--compact">
                      <div className="service-editor__field service-editor__field--half">
                        <Controller
                          name={`variations.${vIndex}.options.${oIndex}.name`}
                          control={control}
                          render={({ field }) => (
                            <div className="form-group">
                              <label className="form-group__label">Option Name *</label>
                              <input
                                {...field}
                                type="text"
                                className={`form-group__input ${
                                  errors.variations?.[vIndex]?.options?.[oIndex]?.name
                                    ? 'form-group__input--error'
                                    : ''
                                }`}
                                placeholder="e.g. Short"
                                disabled={isLoading}
                              />
                              {errors.variations?.[vIndex]?.options?.[oIndex]?.name && (
                                <span className="form-group__error">
                                  {(errors.variations[vIndex] as any).options[oIndex]?.name?.message}
                                </span>
                              )}
                            </div>
                          )}
                        />
                      </div>
                      <div className="service-editor__field service-editor__field--half">
                        <Controller
                          name={`variations.${vIndex}.options.${oIndex}.description`}
                          control={control}
                          render={({ field }) => (
                            <div className="form-group">
                              <label className="form-group__label">Option Description</label>
                              <input
                                {...field}
                                type="text"
                                className={`form-group__input ${
                                  errors.variations?.[vIndex]?.options?.[oIndex]?.description
                                    ? 'form-group__input--error'
                                    : ''
                                }`}
                                placeholder="e.g. Above shoulders"
                                disabled={isLoading}
                              />
                              {errors.variations?.[vIndex]?.options?.[oIndex]?.description && (
                                <span className="form-group__error">
                                  {(errors.variations[vIndex] as any).options[oIndex]?.description?.message}
                                </span>
                              )}
                            </div>
                          )}
                        />
                      </div>
                      <div className="service-editor__field service-editor__field--half">
                        <Controller
                          name={`variations.${vIndex}.options.${oIndex}.additionalPrice`}
                          control={control}
                          render={({ field }) => (
                            <div className="form-group">
                              <label className="form-group__label">Additional Price *</label>
                              <div className="form-group__input-wrapper">
                                <input
                                  {...field}
                                  type="number"
                                  value={field.value !== undefined ? String(field.value) : ''}
                                  className={`form-group__input ${
                                    errors.variations?.[vIndex]?.options?.[oIndex]?.additionalPrice
                                      ? 'form-group__input--error'
                                      : ''
                                  }`}
                                  disabled={isLoading}
                                />
                              </div>
                              {errors.variations?.[vIndex]?.options?.[oIndex]?.additionalPrice && (
                                <span className="form-group__error">
                                  {(errors.variations[vIndex] as any).options[oIndex]?.additionalPrice?.message}
                                </span>
                              )}
                            </div>
                          )}
                        />
                      </div>
                      <div className="service-editor__field service-editor__field--half">
                        <Controller
                          name={`variations.${vIndex}.options.${oIndex}.additionalDuration`}
                          control={control}
                          render={({ field }) => (
                            <div className="form-group">
                              <label className="form-group__label">Additional Duration *</label>
                              <div className="form-group__input-wrapper">
                                <input
                                  {...field}
                                  type="number"
                                  value={field.value !== undefined ? String(field.value) : ''}
                                  className={`form-group__input ${
                                    errors.variations?.[vIndex]?.options?.[oIndex]?.additionalDuration
                                      ? 'form-group__input--error'
                                      : ''
                                  }`}
                                  disabled={isLoading}
                                />
                                <span className="form-group__suffix">min</span>
                              </div>
                              {errors.variations?.[vIndex]?.options?.[oIndex]?.additionalDuration && (
                                <span className="form-group__error">
                                  {(errors.variations[vIndex] as any).options[oIndex]?.additionalDuration?.message}
                                </span>
                              )}
                            </div>
                          )}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              <button
                type="button"
                className="option-card__add"
                onClick={() => handleAddOption(vIndex)}
                disabled={isLoading}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
                Add Option
              </button>
            </div>
          );
          })
        )}
      </TabPanel>

      {/* Form Actions */}
      <div className="tesla-form-actions service-editor__actions">
        <button
          type="button"
          className="tesla-button tesla-button--secondary"
          onClick={onCancel}
          disabled={isLoading}
        >
          Cancel
        </button>
        <div className="tesla-button-group">
          {tabValue < 2 && (
            <button
              type="button"
              className="tesla-button tesla-button--outline"
              onClick={() => setTabValue(tabValue + 1)}
              disabled={isLoading}
            >
              Next
            </button>
          )}
          <button
            type="submit"
            className="tesla-button tesla-button--primary"
            disabled={isLoading}
          >
            {initialData ? 'Update Service' : 'Create Service'}
          </button>
        </div>
      </div>
    </form>
  );
};

export default ServiceEditorForm;
